/** The Payment section of the "New booking" form: the payment-link/comp
 *  radio pair (Comp permission-gated, "Manager" lock badge when disabled) +
 *  the comp-only required Reason textarea. */
import { Lock } from 'lucide-react';

import {
  inputClass,
  labelClass,
  RESERVATIONS_ENABLED,
  type PaymentMode,
} from './CreateBookingModalHelpers';
import { CreateBookingSectionHeader } from './CreateBookingSectionHeader';

export interface PaymentFieldsProps {
  mode: PaymentMode;
  setMode: (v: PaymentMode) => void;
  reason: string;
  setReason: (v: string) => void;
  canComp: boolean;
}

function PaymentModeOption({
  active,
  disabled,
  title,
  subtitle,
  badge,
  onSelect,
}: {
  active: boolean;
  disabled: boolean;
  title: string;
  subtitle: string;
  badge?: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
        disabled
          ? 'cursor-not-allowed border-stone-200 opacity-50 dark:border-stone-700'
          : active
            ? 'border-moss-600 bg-moss-50/60 dark:bg-moss-900/20'
            : 'border-stone-200 hover:border-stone-300 dark:border-stone-700'
      }`}
    >
      <span
        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${
          active ? 'border-moss-600' : 'border-stone-300 dark:border-stone-600'
        }`}
      >
        {active && <span className="h-2 w-2 rounded-full bg-moss-600" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-bark-900 dark:text-stone-100">{title}</span>
        <span className="block text-xs text-stone-500 dark:text-stone-400">{subtitle}</span>
      </span>
      {badge}
    </button>
  );
}

export function PaymentFields(props: PaymentFieldsProps) {
  return (
    <div>
      <CreateBookingSectionHeader hint="always through Stripe — no card details here">
        Payment
      </CreateBookingSectionHeader>
      <div role="radiogroup" aria-label="Payment method" className="space-y-2">
        <PaymentModeOption
          active={props.mode === 'reservation'}
          disabled={!RESERVATIONS_ENABLED}
          title="Reserve seats — customer pays"
          subtitle={
            RESERVATIONS_ENABLED
              ? 'We email them a link · seats held 24h'
              : 'Temporarily unavailable'
          }
          onSelect={() => RESERVATIONS_ENABLED && props.setMode('reservation')}
        />
        <PaymentModeOption
          active={props.mode === 'comp'}
          disabled={!props.canComp}
          title="Comp — free booking"
          subtitle={props.canComp ? 'No payment collected' : 'Only managers can comp'}
          onSelect={() => props.canComp && props.setMode('comp')}
          badge={
            !props.canComp && (
              <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-clay-100 px-2.5 py-1 text-xs font-semibold text-clay-700 dark:bg-clay-900/30 dark:text-clay-300">
                <Lock size={11} />
                Manager
              </span>
            )
          }
        />
      </div>
      {props.mode === 'comp' && (
        <div className="mt-3">
          <label className={labelClass}>
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={props.reason}
            onChange={(e) => props.setReason(e.target.value)}
            rows={2}
            className={inputClass}
            placeholder="Why is this booking comped?"
            required
          />
        </div>
      )}
    </div>
  );
}
